/* eslint-disable no-constant-condition */
/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { IMantenance } from '../api/maintenances/MaintenancesServices';
import { format, addDays, getYear } from 'date-fns';
import parseISO from 'date-fns/parseISO';
import { ptBR } from 'date-fns/locale';
import { Filesystem, Directory } from '@capacitor/filesystem';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import type { TypeEstruturas } from '../../components/custom/Reports/Risrot/risrotEstruturaCatalog';

export interface CEP {
	cep: string;
	logradouro: string;
	complemento: string;
	unidade: string;
	bairro: string;
	localidade: string;
	uf: string;
	ibge: string;
	gia: string;
	ddd: string;
	siafi: string;
}

const getAdress = async (cep: string): Promise<CEP | Error> => {
	try {
		const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);

		if (response.data) {
			return response.data;
		}

		return new Error('Erro na consulta.');
	} catch (error) {
		return new Error(
			(error as { menssage: string }).menssage || 'Erro na consulta'
		);
	}
};

function base64ToImg(base64String: string): File {
	const bstr = atob(base64String);
	let n = bstr.length;
	const u8arr = new Uint8Array(n);

	while (n--) {
		u8arr[n] = bstr.charCodeAt(n);
	}

	return new File([u8arr], 'image', { type: 'image/png' });
}

export function base64ToFile(
	base64String: string | undefined | File,
	fileName: string,
	mimeTypeOverride?: string
): File | undefined {
	if (!base64String) return;
	if (base64String instanceof File) return base64String;

	let mimeType = mimeTypeOverride || 'application/octet-stream';
	let base64Data = base64String;

	const matches = base64String.match(/^data:(.*?);base64,(.*)$/);

	if (matches) {
		mimeType = matches[1];
		base64Data = matches[2];
	}

	try {
		const byteString = atob(base64Data);
		const byteNumbers = new Array(byteString.length);

		for (let i = 0; i < byteString.length; i++) {
			byteNumbers[i] = byteString.charCodeAt(i);
		}

		const byteArray = new Uint8Array(byteNumbers);

		return new File([byteArray], fileName, { type: mimeType });
	} catch (err) {
		return;
	}
}

function formatUtmNS(utmNs: string) {
	const parsedValue = parseFloat(utmNs);
	if (!isNaN(parsedValue)) {
		const integerPart = Math.floor(parsedValue).toString().slice(0, 7);
		const decimalPart = utmNs.includes('.')
			? utmNs.split('.')[1].slice(0, 2)
			: '00';
		const formattedValue = parseFloat(`${integerPart}.${decimalPart}`);
		return formattedValue;
	}
}

function formatUtmEw(utmEw: string) {
	const parsedValue = parseFloat(utmEw);
	if (!isNaN(parsedValue)) {
		const integerPart = Math.floor(parsedValue).toString().slice(0, 6);
		const decimalPart = utmEw.includes('.')
			? utmEw.split('.')[1].slice(0, 2)
			: '00';
		const formattedValue = parseFloat(`${integerPart}.${decimalPart}`);
		return formattedValue;
	}
}

const dayOfWeekMap: Record<string, number> = {
	domingo: 0,
	segunda: 1,
	terça: 2,
	terca: 2,
	quarta: 3,
	quinta: 4,
	sexta: 5,
	sábado: 6,
	sabado: 6,
};
// const monthMap: Record<string, string> = {
//   january: 'janeiro',
//   february: 'fevereiro',
//   march: 'março',
//   april: 'abril',
//   may: 'maio',
//   june: 'junho',
//   july: 'julho',
//   august: 'agosto',
//   september: 'setembro',
//   october: 'outubro',
//   november: 'novembro',
//   december: 'dezembro',
// };

const getDatesForDayOfWeek = (dayOfWeek: string) => {
	const dayIndex = dayOfWeekMap[dayOfWeek.toLowerCase().trim()];

	if (dayIndex === undefined) {
		return [];
	}

	const dates = [];
	const today = new Date();
	const currentYear = today.getFullYear();

	for (let monthOffset = 0; monthOffset < 6; monthOffset++) {
		const targetMonth = today.getMonth() + monthOffset;
		const targetYear = currentYear + Math.floor(targetMonth / 12);
		const normalizedMonth = targetMonth % 12;

		let currentDate = new Date(targetYear, normalizedMonth, 1);
		const lastDayOfMonth = new Date(targetYear, normalizedMonth + 1, 0);

		while (currentDate <= lastDayOfMonth) {
			if (currentDate.getDay() === dayIndex) {
				dates.push(format(currentDate, 'dd/MM/yyyy'));
			}
			currentDate = addDays(currentDate, 1);
		}
	}

	return dates;
};

const getDatesForMonthly = (datas: string) => {
	const monthMap: { [key: string]: string } = {
		Janeiro: '01',
		Fevereiro: '02',
		Março: '03',
		Abril: '04',
		Maio: '05',
		Junho: '06',
		Julho: '07',
		Agosto: '08',
		Setembro: '09',
		Outubro: '10',
		Novembro: '11',
		Dezembro: '12',
	};

	try {
		const parsed = JSON.parse(datas);
		const { days, months } = parsed;
		const currentYear = new Date().getFullYear();

		const allDates = days
			.flatMap((day: string) =>
				months.map((monthName: string) => {
					const monthNumber = monthMap[monthName];
					if (!monthNumber) return null;

					const formattedDay = day.padStart(2, '0');
					return `${formattedDay}/${monthNumber}/${currentYear}`;
				})
			)
			.filter(Boolean);

		return allDates as string[];
	} catch (error) {
		return [];
	}
};

const processData = (data: IMantenance[]): FormatedDate[] => {
	const results: FormatedDate[] = [];

	data.forEach((item) => {
		let processedDates: string[] = [];
		if (item.datas) {
			if (item.frequencia === 'semanal') {
				processedDates = getDatesForDayOfWeek(item.datas);
			} else if (item.frequencia === 'mensal') {
				processedDates = getDatesForMonthly(item.datas);
			} else if (item.frequencia === 'unica') {
				processedDates = [format(parseISO(item.datas), 'dd/MM/yyyy')];
			} else if (item.frequencia === 'quinzenal') {
				const dates: string[] = [];
				let currentDate = parseISO(item.datas);
				const startYear = getYear(currentDate);

				while (getYear(currentDate) === startYear) {
					dates.push(format(currentDate, 'dd/MM/yyyy'));
					currentDate = addDays(currentDate, 15);
				}
				processedDates = dates;
			}

			results.push({
				date: processedDates.join(', '),
				uuid: item.uuid,
				frequencia: item.frequencia,
				descricao: item.descricao,
				tempo: item.tempo,
				nome: item.nome,
			});
		}
	});

	return results;
};

export const formatDate = (dateString: string | null | undefined) => {
	try {
		if (!dateString) {
			return 'Data não informada';
		}
		if (
			typeof dateString === 'string' &&
			dateString.match(/^\d{2}\/\d{2}\/\d{4}/)
		) {
			return dateString;
		}

		let date: Date;

		if (
			typeof dateString === 'string' &&
			!dateString.includes('Z') &&
			!dateString.includes('+') &&
			!dateString.includes('-')
		) {
			date = new Date(dateString + ' GMT-0300');
		} else {
			date = new Date(dateString);
		}

		if (isNaN(date.getTime())) {
			throw new Error(`Data inválida: ${dateString}`);
		}

		return format(date, 'dd/MM/yyyy HH:mm:ss', { locale: ptBR });
	} catch (error) {
		return 'Erro na data';
	}
};

export const formatDateWhitoutHour = (
	dateString: string | null | undefined
) => {
	try {
		if (!dateString) {
			return 'Data não informada';
		}
		if (
			typeof dateString === 'string' &&
			dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)
		) {
			return dateString;
		}

		let date: Date;

		if (
			typeof dateString === 'string' &&
			!dateString.includes('Z') &&
			!dateString.includes('+') &&
			!dateString.includes('-')
		) {
			const parts = dateString.split(' ');
			if (parts.length === 2) {
				date = new Date(`${parts[0]}T${parts[1]}`);
			} else {
				date = parseISO(dateString);
			}
		} else {
			date = parseISO(dateString);
		}

		if (isNaN(date.getTime())) {
			throw new Error(`Data inválida: ${dateString}`);
		}

		return format(date, 'dd/MM/yyyy', { locale: ptBR });
	} catch (error) {
		return 'Erro na data';
	}
};

export const formatDateWhitoutHourEUA = (
	dateString: string | null | undefined
) => {
	try {
		if (!dateString) {
			return 'Data não informada';
		}
		if (
			typeof dateString === 'string' &&
			dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)
		) {
			return dateString;
		}

		let date: Date;

		if (
			typeof dateString === 'string' &&
			!dateString.includes('Z') &&
			!dateString.includes('+') &&
			!dateString.includes('-')
		) {
			const parts = dateString.split(' ');
			if (parts.length === 2) {
				date = new Date(`${parts[0]}T${parts[1]}`);
			} else {
				date = new Date(dateString);
			}
		} else {
			date = new Date(dateString);
		}

		if (isNaN(date.getTime())) {
			throw new Error(`Data inválida: ${dateString}`);
		}

		return format(date, 'dd/MM/yyyy', { locale: ptBR });
	} catch (error) {
		return 'Erro na data';
	}
};

export interface FormatedDate {
	date: string;
	uuid?: string;
	frequencia: string;
	descricao: string;
	tempo?: string;
	nome: string;
}

export const getUniqueFileName = async (
	originalFileName: string,
	directory: Directory
): Promise<string> => {
	const dotIndex = originalFileName.lastIndexOf('.');
	const nameWithoutExt =
		dotIndex > -1 ? originalFileName.substring(0, dotIndex) : originalFileName;
	const extension = dotIndex > -1 ? originalFileName.substring(dotIndex) : '';

	let counter = 0;
	let finalFileName = originalFileName;

	while (true) {
		let checkPath = finalFileName;

		if (counter > 0) {
			checkPath = `${nameWithoutExt}(${counter})${extension}`;
		}

		try {
			await Filesystem.stat({
				path: checkPath,
				directory: directory,
			});
			finalFileName = checkPath;
			counter++;
		} catch (e: any) {
			if (e.code === 'OS-PLUG-FILE-0008') {
				return checkPath;
			} else {
				throw e;
			}
		}
	}
};

function formatDateMaintenance(date: string) {
	const datas = date.split(',').map((d) => d.trim());

	if (datas.length <= 1) {
		return date;
	}

	const first = datas[0];
	const last = datas[datas.length - 1];

	return `${first} ... ${last}`;
}

const convertDateManual = (dateString: string | null | undefined): string => {
	if (!dateString) {
		return 'Data não informada';
	}

	const parts = dateString.split('-');

	if (parts.length === 3) {
		const [year, month, day] = parts;
		return `${day}/${month}/${year}`;
	}

	return 'Formato inválido';
};

function formatXAxisDate(dateValue: any) {
	if (typeof dateValue === 'string' || typeof dateValue === 'number') {
		const date = new Date(dateValue);
		return date.toLocaleDateString('pt-BR');
	}
	return dateValue;
}

export function formatDateForInputDate(
	dateStr: string | null | undefined
): string | null {
	if (!dateStr) return null;

	const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (!match) {
		return null;
	}

	const [, year, month, day] = match;
	return `${year}-${month}-${day}`;
}

export async function gerarPdfDeComponentes(
	estruturas: TypeEstruturas[],
	renderComponente: (estrutura: TypeEstruturas) => JSX.Element,
	componenteInicial?: JSX.Element
) {
	const doc = new jsPDF();
	const container = document.createElement('div');
	container.style.position = 'absolute';
	container.style.top = '-9999px';
	container.style.left = '-9999px';
	document.body.appendChild(container);

	if (componenteInicial) {
		const wrapperInicial = document.createElement('div');
		container.appendChild(wrapperInicial);

		const root = await import('react-dom/client');
		const reactRoot = root.createRoot(wrapperInicial);
		reactRoot.render(componenteInicial);

		await new Promise((resolve) => setTimeout(resolve, 300));

		const canvas = await html2canvas(wrapperInicial, {
			scale: 0.5,
			useCORS: true,
		});
		const imgData = canvas.toDataURL('image/png');
		const imgWidth = 210;
		const imgHeight = (canvas.height * imgWidth) / canvas.width;

		doc.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
	}

	for (let i = 0; i < estruturas.length; i++) {
		const estrutura = estruturas[i];

		const wrapper = document.createElement('div');
		container.appendChild(wrapper);

		const reactElement = renderComponente(estrutura);
		const root = await import('react-dom/client');
		const reactRoot = root.createRoot(wrapper);
		reactRoot.render(reactElement);

		await new Promise((resolve) => setTimeout(resolve, 100));

		const canvas = await html2canvas(wrapper, {
			scale: 0.5,
			useCORS: true,
		});
		const imgData = canvas.toDataURL('image/png');

		const imgWidth = 210;
		const imgHeight = (canvas.height * imgWidth) / canvas.width;

		doc.addPage();
		doc.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
	}

	document.body.removeChild(container);
	doc.save('estruturas.pdf');
	return false;
}

function base64ToBlob(base64: string, mimeType: string): Blob {
	const byteCharacters = atob(base64);

	const byteNumbers = new Array(byteCharacters.length);
	for (let i = 0; i < byteCharacters.length; i++) {
		byteNumbers[i] = byteCharacters.charCodeAt(i);
	}
	const byteArray = new Uint8Array(byteNumbers);
	return new Blob([byteArray], { type: mimeType });
}

function mapNomeInstrumento(nome: string) {
	switch (nome) {
		case 'piezometro':
			return 'Piezômetro';
		case 'indicador':
			return 'Indicador nível da água';
		case 'poco':
			return 'Poço de alívio';
		case 'vazao':
			return 'Medidor de vazão';
		case 'regua':
			return 'Régua de reservatorio';
		case 'marco':
			return 'Marco topográfico';
		case 're':
			return 'Ré topográfico';
		case 'base':
			return 'Base da estação total';
		case 'inclinometro':
			return 'Inclinômetro';
		case 'pluviometro':
			return 'Pluviômetrico';
	}
}


function mesConvertBR(data: string | null) {
	if (data === '') {
		return null;
	}

	if (data === null) {
		return null;
	}

	if (data.includes('/')) {
		return data;
	}
	const [ano, mes] = data.split('-');
	const newDate = `${mes}/${ano}`;
	return newDate;
}

function mesConvertEUA(data: string | null) {

	if (data === null) {
		return null;
	}

	if (data.includes('-')) {
		return data;
	}
	const [mes, ano] = data.split('/');
	const newDate = `${ano}-${mes}`;
	return newDate;
}

const fileToBase64WithValidation = (file: File): Promise<string> => {
	return new Promise((resolve, reject) => {
		// Verifica se é uma imagem
		if (!file.type.startsWith('image/')) {
			reject(new Error('O arquivo selecionado não é uma imagem'));
			return;
		}

		// Verifica tamanho máximo (opcional - 5MB exemplo)
		const maxSize = 5 * 1024 * 1024; // 5MB
		if (file.size > maxSize) {
			reject(new Error('A imagem deve ter no máximo 5MB'));
			return;
		}

		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = error => reject(error);
	});
};

export const helpers = {
	getAdress,
	base64ToImg,
	base64ToFile,
	formatUtmNS,
	formatUtmEw,
	formatDateMaintenance,
	processData,
	convertDateManual,
	formatXAxisDate,
	gerarPdfDeComponentes,
	mapNomeInstrumento,
	base64ToBlob,
	mesConvertBR,
	mesConvertEUA,
	fileToBase64WithValidation
};
